/**
 * User types and enums for client-side use
 *
 * IMPORTANT: This file must NOT import from @prisma/client to avoid
 * bundling Prisma in the client code.
 *
 * These types mirror the Prisma enums but are safe for client use.
 */

// ============= User Roles =============

export enum UserRole {
  STUDENT = 'STUDENT',
  TEACHER = 'TEACHER',
  DEVELOPER = 'DEVELOPER',
  SCHOOL_ADMIN = 'SCHOOL_ADMIN',
  DISTRICT_ADMIN = 'DISTRICT_ADMIN',
  ADMIN = 'ADMIN',
}

export type UserRoleType = keyof typeof UserRole;

// ============= Pricing & Licensing =============

export enum PricingModel {
  FREE = 'FREE',
  ONE_TIME = 'ONE_TIME',
  SUBSCRIPTION = 'SUBSCRIPTION',
  FREEMIUM = 'FREEMIUM',
  USAGE_BASED = 'USAGE_BASED',
}

export enum LicenseType {
  INDIVIDUAL = 'INDIVIDUAL',
  CLASSROOM = 'CLASSROOM',
  SCHOOL = 'SCHOOL',
  DISTRICT = 'DISTRICT',
}

export enum SubscriptionStatus {
  ACTIVE = 'ACTIVE',
  CANCELED = 'CANCELED',
  PAST_DUE = 'PAST_DUE',
  TRIALING = 'TRIALING',
  PAUSED = 'PAUSED',
  INCOMPLETE = 'INCOMPLETE',
}

// ============= App Status =============

export enum AppStatus {
  DRAFT = 'DRAFT',
  SUBMITTED = 'SUBMITTED',
  IN_REVIEW = 'IN_REVIEW',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
  PUBLISHED = 'PUBLISHED',
  UNPUBLISHED = 'UNPUBLISHED',
  ARCHIVED = 'ARCHIVED',
}

export enum AppStorageType {
  DIRECT = 'DIRECT',
  GITHUB = 'GITHUB',
  MIGRATING = 'MIGRATING',
}

// Role hierarchy for permission checking
export const ROLE_HIERARCHY: Record<UserRole, number> = {
  [UserRole.STUDENT]: 0,
  [UserRole.TEACHER]: 1,
  [UserRole.DEVELOPER]: 2,
  [UserRole.SCHOOL_ADMIN]: 3,
  [UserRole.DISTRICT_ADMIN]: 4,
  [UserRole.ADMIN]: 5,
};

// Helper to check if a role has minimum permission level
export function hasMinimumRole(userRole: UserRole, requiredRole: UserRole): boolean {
  return ROLE_HIERARCHY[userRole] >= ROLE_HIERARCHY[requiredRole];
}

// Helper to check if user has one of the allowed roles
export function hasRole(userRole: UserRole, allowedRoles: UserRole | UserRole[]): boolean {
  const roles = Array.isArray(allowedRoles) ? allowedRoles : [allowedRoles];
  return roles.includes(userRole);
}
